import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Cog6ToothIcon, ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

const FEATURES = {
  dashboard: {
    name: 'Dashboard & Overview',
    description: 'Access to dashboard statistics and activity summaries',
    permissions: {
      viewStats: 'View dashboard statistics and metrics',
      viewActivity: 'View activity summaries and alerts'
    }
  },
  reservations: {
    name: 'Reservations Management',
    description: 'Manage bookings and reservations',
    permissions: {
      view: 'View all reservations and booking details',
      create: 'Create new reservations and bookings',
      edit: 'Edit existing reservations (modify dates, customer info)',
      cancel: 'Cancel reservations and process refunds',
      processReturn: 'Process returns (mark as complete, handle overages)',
      reprocess: 'Reprocess returns (modify completion details)',
      delete: 'Delete reservations permanently'
    }
  },
  customers: {
    name: 'Customer Management',
    description: 'Manage customer information and profiles',
    permissions: {
      view: 'View customer information and history',
      create: 'Create new customer profiles',
      edit: 'Edit customer profiles and contact details',
      delete: 'Delete customer profiles'
    }
  },
  inventoryItems: {
    name: 'Physical Items & Equipment',
    description: 'Manage inventory items and equipment',
    permissions: {
      view: 'View inventory items and their status',
      create: 'Add new inventory items',
      edit: 'Edit inventory items and availability',
      delete: 'Delete inventory items'
    }
  },
  productTypes: {
    name: 'Product Types & Categories',
    description: 'Manage product categories, types and pricing',
    permissions: {
      view: 'View product types and variants',
      create: 'Create new product types and categories',
      edit: 'Edit product types, pricing and rates',
      delete: 'Delete product types and categories'
    }
  },
  inventoryReports: {
    name: 'Inventory Analytics',
    description: 'Access inventory reports and analytics',
    permissions: {
      view: 'View inventory utilization reports',
      export: 'Export inventory performance data'
    }
  },
  services: {
    name: 'Services & Bookings',
    description: 'Manage service offerings and bookings',
    permissions: {
      view: 'View all services and availability',
      create: 'Create new service offerings',
      edit: 'Edit service descriptions and pricing',
      delete: 'Delete services'
    }
  },
  serviceBookings: {
    name: 'Service Reservations',
    description: 'Process service bookings and schedules',
    permissions: {
      view: 'View service reservations and schedules',
      process: 'Process service reservations and manage slots',
      checkIn: 'Check in customers to service reservations',
      cancel: 'Cancel service bookings'
    }
  },
  payments: {
    name: 'Financial Operations',
    description: 'Handle payments and financial transactions',
    permissions: {
      takePayment: 'Take payments (cash, credit, other methods)',
      processRefund: 'Process refunds and adjustments',
      viewHistory: 'View payment history and summaries'
    }
  },
  timesheets: {
    name: 'Staff & Time Management',
    description: 'Manage employee timesheets and time tracking',
    permissions: {
      view: 'View employee timesheets',
      clockInOut: 'Use clock in/out functionality',
      approve: 'Approve timesheet entries'
    }
  },
  reports: {
    name: 'Reports & Analytics',
    description: 'Generate and access business reports',
    permissions: {
      view: 'View business reports and analytics',
      generate: 'Generate new reports',
      export: 'Export report data'
    }
  },
  communications: {
    name: 'Communications',
    description: 'Manage emails and customer communications',
    permissions: {
      sendEmails: 'Send emails to customers',
      editTemplates: 'Edit email templates and notifications'
    }
  },
  userManagement: {
    name: 'User Management',
    description: 'Manage system users and roles',
    permissions: {
      viewUsers: 'View user accounts and profiles',
      createUser: 'Create new employee accounts',
      editUser: 'Edit employee profiles and details',
      deleteUser: 'Delete or deactivate employee accounts',
      manageRoles: 'Manage user permissions and roles'
    }
  },
  systemSettings: {
    name: 'System Administration',
    description: 'Configure system settings and locations',
    permissions: {
      manageLocations: 'Manage business locations',
      configureSystem: 'Access administrative settings'
    }
  }
};

const ROLES = ['EMPLOYEE', 'MANAGER'];

const UsersPermissions = () => {
  const [activeTab, setActiveTab] = useState('matrix');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [policy, setPolicy] = useState({});
  const [logs, setLogs] = useState([]);

  const defaultPolicy = useMemo(() => {
    const p = {};
    // Only create policy for Employee and Manager roles
    // Administrator always has all permissions and is not configurable
    ROLES.forEach(role => {
      p[role] = {};
      Object.entries(FEATURES).forEach(([featureKey, featureData]) => {
        p[role][featureKey] = {};
        Object.keys(featureData.permissions).forEach(permission => { 
          p[role][featureKey][permission] = true; 
        });
      });
    });
    return p;
  }, []);

  const loadPolicy = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await axios.get('/api/settings/permissions');
      setPolicy(resp.data?.policy || defaultPolicy);
    } catch (err) {
      console.error('Load permissions error:', err);
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [defaultPolicy]);

  const loadLogs = async () => {
    try {
      const resp = await axios.get('/api/settings/permissions/logs');
      setLogs(resp.data?.logs || []);
    } catch (err) {
      console.error('Load permission logs error:', err);
    }
  };

  useEffect(() => { loadPolicy(); }, [loadPolicy]);
  useEffect(() => { if (activeTab === 'logs') loadLogs(); }, [activeTab]);

  const toggle = (role, feature, permission) => {
    setPolicy(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [feature]: {
          ...prev[role]?.[feature],
          [permission]: !prev[role]?.[feature]?.[permission]
        }
      }
    }));
  };

  const toggleAllInFeature = (role, feature, enabled) => {
    setPolicy(prev => {
      const newFeaturePerms = {};
      Object.keys(FEATURES[feature].permissions).forEach(permission => {
        newFeaturePerms[permission] = enabled;
      });
      
      return {
        ...prev,
        [role]: {
          ...prev[role],
          [feature]: newFeaturePerms
        }
      };
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await axios.put('/api/settings/permissions', { policy, changeNote: 'Updated role permissions' });
      setSuccess('Permissions updated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Save permissions error:', err);
      const msg = err.response?.data?.message || 'Failed to update permissions';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Users & Permissions
          </h2>
          <p className="mt-1 text-gray-600">Manage action-level permissions per role. Administrators have all permissions by default.</p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center gap-2">
          <button onClick={loadPolicy} className="inline-flex items-center px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm">
            <ArrowPathIcon className="h-4 w-4 mr-1" /> Refresh
          </button>
          <button onClick={save} disabled={saving} className="inline-flex items-center px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50">
            <Cog6ToothIcon className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg">
        <div className="border-b px-4 py-2">
          <nav className="flex gap-4 text-sm">
            <button className={`py-2 ${activeTab==='matrix'?'text-blue-600 border-b-2 border-blue-600':'text-gray-600'}`} onClick={()=>setActiveTab('matrix')}>Permissions Matrix</button>
            <button className={`py-2 ${activeTab==='logs'?'text-blue-600 border-b-2 border-blue-600':'text-gray-600'}`} onClick={()=>setActiveTab('logs')}>Change Log</button>
          </nav>
        </div>

        {activeTab==='matrix' ? (
          <div className="p-4 overflow-auto">
            {error && (
              <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 inline-flex items-center"><ExclamationTriangleIcon className="h-4 w-4 mr-1" /> {error}</div>
            )}
            {success && (
              <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2 inline-flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> {success}</div>
            )}
            {loading ? (
              <div className="flex items-center text-sm text-gray-600"><ClockIcon className="h-4 w-4 mr-2" /> Loading permissions...</div>
            ) : (
              <div className="space-y-6">
                {Object.entries(FEATURES).map(([featureKey, featureData]) => (
                  <div key={featureKey} className="border border-gray-200 rounded-lg">
                    {/* Feature Header */}
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{featureData.name}</h3>
                          <p className="text-sm text-gray-600">{featureData.description}</p>
                        </div>
                        <div className="flex items-center space-x-8">
                          {ROLES.map(role => (
                            <div key={role} className="text-center min-w-[100px]">
                              <label className="block text-xs font-medium text-gray-500 mb-1">{role}</label>
                              <button
                                onClick={() => {
                                  const allEnabled = Object.keys(featureData.permissions).every(
                                    perm => policy?.[role]?.[featureKey]?.[perm]
                                  );
                                  toggleAllInFeature(role, featureKey, !allEnabled);
                                }}
                                className="px-2 py-1 text-xs border rounded hover:bg-gray-100"
                              >
                                {Object.keys(featureData.permissions).every(
                                  perm => policy?.[role]?.[featureKey]?.[perm]
                                ) ? 'Remove All' : 'Grant All'}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Permissions List */}
                    <div className="divide-y divide-gray-100">
                      {Object.entries(featureData.permissions).map(([permissionKey, permissionDesc]) => (
                        <div key={`${featureKey}.${permissionKey}`} className="px-4 py-3 flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{permissionDesc}</div>
                            <div className="text-xs text-gray-500 mt-1">Permission: {featureKey}.{permissionKey}</div>
                          </div>
                          <div className="flex items-center space-x-8">
                            {ROLES.map(role => (
                              <div key={`${role}.${featureKey}.${permissionKey}`} className="flex justify-center min-w-[100px]">
                                <input
                                  type="checkbox"
                                  id={`${role}-${featureKey}-${permissionKey}`}
                                  checked={!!policy?.[role]?.[featureKey]?.[permissionKey]}
                                  onChange={() => toggle(role, featureKey, permissionKey)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor={`${role}-${featureKey}-${permissionKey}`} className="sr-only">
                                  {role} - {permissionDesc}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="p-4">
            {logs.length === 0 ? (
              <div className="text-sm text-gray-600">No changes recorded yet.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-500">Time</th>
                    <th className="px-3 py-2 text-left text-gray-500">Actor</th>
                    <th className="px-3 py-2 text-left text-gray-500">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className="border-t">
                      <td className="px-3 py-2">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-3 py-2">{log.actorName || 'Unknown'}</td>
                      <td className="px-3 py-2">{log.changeNote || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPermissions;